'use client';

import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import {
  CMS_SHELL_PREVIEW_AUTHENTICATED_CREDITS,
  type CmsShellViewerMode,
} from '@/cms/chrome/shell-viewer-mode';
import { WorkspaceMobileDrawer } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-mobile-drawer';
import { useWorkspaceIsMobileViewport } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import {
  type CmsAccountLocalCopy,
  CmsAccountMenuContent,
  buildCmsAccountMenuSections,
  useCmsAccountLocalCopy,
  useCmsAccountSignOut,
} from '@/cms/components/chrome/cms-account-menu';
import { useCmsAccountSettingsDialog } from '@/cms/components/chrome/cms-account-settings-state';
import { CmsRemainingCreditsSlot } from '@/cms/components/chrome/cms-remaining-credits-slot';
import { CmsShellViewerState } from '@/cms/components/chrome/cms-shell-viewer-state';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { UserAvatar } from '@/components/layout/user/user-avatar';
import {
  PremiumCrownIcon,
  PremiumGradientText,
} from '@/components/paid-access/premium-crown-icon';
import { useCurrentPlan, usePaidAccess } from '@/hooks/payment/use-payment';
import { isHighestSubscriptionTier } from '@/lib/pricing/subscription-tier';
import { cn } from '@/lib/ui/utils';
import { type ReactNode, useMemo, useState } from 'react';
import type { CmsWorkspaceSidebarUtilityItem } from './cms-workspace-sidebar-utility-item';
import styles from './cms-workspace-sidebar.module.css';

type CmsWorkspaceSidebarAccountUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

export type CmsWorkspaceSubscriptionActionRenderer = (
  labelOverride?: string,
  surface?: 'account-menu' | 'sidebar'
) => ReactNode;

const sidebarAccountMenuContentClassNames = {
  panel: styles.accountMenuPanel,
  user: styles.accountMenuUser,
  userRow: styles.accountMenuUserRow,
  userAvatar: styles.accountMenuUserAvatar,
  userIdentity: styles.accountMenuUserIdentity,
  userMeta: styles.accountMenuUserMeta,
  userName: styles.accountMenuUserName,
  userEmail: styles.accountMenuUserEmail,
  separator: styles.accountMenuSeparator,
  sectionTitle: styles.accountMenuSectionTitle,
  item: styles.accountMenuItem,
  itemIcon: styles.accountMenuItemIcon,
};

const mobileSidebarAccountMenuContentClassNames = {
  ...sidebarAccountMenuContentClassNames,
  panel: styles.mobileAccountMenuPanel,
  item: cn(styles.accountMenuItem, styles.mobileAccountMenuItem),
  itemIcon: cn(styles.accountMenuItemIcon, styles.mobileAccountMenuItemIcon),
};

function CmsWorkspaceAccountPlanCard({
  accountMenuCopy,
  credits,
  creditsLabel,
  onOpenSettings,
  paid,
  planLabel,
  subscriptionAction,
}: {
  accountMenuCopy: CmsAccountLocalCopy;
  credits: ReactNode;
  creditsLabel: string;
  onOpenSettings: (
    section: Parameters<
      ReturnType<typeof useCmsAccountSettingsDialog>['openSettings']
    >[0]
  ) => void;
  paid: boolean;
  planLabel?: string;
  subscriptionAction?: ReactNode;
}) {
  return (
    <div className={styles.accountMenuPlanCard} data-account-menu-plan-card="">
      {planLabel ? (
        <button
          aria-label={accountMenuCopy.billingLabel}
          className={cn(
            styles.accountMenuPlanLabel,
            styles.accountMenuPlanLabelButton
          )}
          data-account-menu-plan-section="billing"
          onClick={() => onOpenSettings('billing')}
          type="button"
        >
          {paid ? (
            <>
              <PremiumCrownIcon
                iconClassName={styles.accountMenuPlanCrownIcon}
                opticalOffsetY="0"
                showTooltip={false}
                title={planLabel}
              />
              <PremiumGradientText>{planLabel}</PremiumGradientText>
            </>
          ) : (
            planLabel
          )}
        </button>
      ) : (
        <div
          aria-hidden="true"
          className={styles.accountMenuPlanLabel}
          data-account-menu-plan-skeleton=""
        >
          <span
            className={cn(
              styles.accountSkeletonBlock,
              styles.accountMenuPlanLabelSkeleton
            )}
          />
        </div>
      )}
      <button
        aria-label={creditsLabel}
        className={styles.accountMenuPlanBalance}
        data-account-menu-credits-section="credits"
        onClick={() => onOpenSettings('credits')}
        type="button"
      >
        {credits}
        <span className={styles.accountMenuPlanBalanceLabel}>
          {creditsLabel}
        </span>
      </button>
      {subscriptionAction ? (
        <div className={styles.accountMenuPlanAction}>{subscriptionAction}</div>
      ) : null}
    </div>
  );
}

function CmsWorkspaceSidebarAccountMenu({
  accountMenuCopy,
  accountMenuCredits,
  credits,
  creditsLabel,
  expanded,
  locale,
  mode,
  renderSubscriptionAction,
  user,
}: {
  accountMenuCopy: CmsAccountLocalCopy;
  accountMenuCredits: ReactNode;
  credits: ReactNode;
  creditsLabel: string;
  expanded: boolean;
  locale: string;
  mode: CmsShellViewerMode;
  renderSubscriptionAction?: CmsWorkspaceSubscriptionActionRenderer;
  user: CmsWorkspaceSidebarAccountUser;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const settingsDialog = useCmsAccountSettingsDialog();
  const isMobileViewport = useWorkspaceIsMobileViewport();
  const { data: paidAccessState } = usePaidAccess(user.id, {
    enabled: mode === 'live',
  });
  const { data: billingState } = useCurrentPlan(user.id, {
    enabled: mode === 'live' && paidAccessState?.hasSubscriptionAccess === true,
  });
  const sections = useMemo(
    () => buildCmsAccountMenuSections(accountMenuCopy),
    [accountMenuCopy]
  );
  const { handleSignOut } = useCmsAccountSignOut(locale, accountMenuCopy);
  const hasResolvedPaidAccess =
    mode === 'authenticated' || paidAccessState !== undefined;
  const isPaidUser =
    mode === 'authenticated' || paidAccessState?.hasPaidAccess === true;
  const isHighestSubscription =
    mode === 'live' &&
    paidAccessState?.hasSubscriptionAccess === true &&
    billingState !== undefined &&
    isHighestSubscriptionTier(billingState);
  const subscriptionActionLabel = isHighestSubscription
    ? accountMenuCopy.getMoreLabel
    : accountMenuCopy.upgradeLabel;
  const subscriptionAction = renderSubscriptionAction?.(
    subscriptionActionLabel,
    'sidebar'
  );
  const accountMenuSubscriptionAction = renderSubscriptionAction?.(
    subscriptionActionLabel,
    'account-menu'
  );
  const planLabel = hasResolvedPaidAccess
    ? isPaidUser
      ? accountMenuCopy.paidPlanLabel
      : accountMenuCopy.freePlanLabel
    : undefined;
  const openSettings = (
    section: Parameters<typeof settingsDialog.openSettings>[0]
  ) => {
    setMobileOpen(false);
    settingsDialog.openSettings(section);
  };

  return (
    <div
      className={cn(
        styles.accountSummary,
        expanded && styles.accountSummaryExpanded
      )}
    >
      {expanded ? (
        <div
          className={styles.accountCommerceRow}
          data-sidebar-account-commerce=""
        >
          <button
            type="button"
            className={styles.accountBalanceAction}
            aria-label={creditsLabel}
            data-sidebar-account-balance=""
            onClick={() => openSettings('credits')}
          >
            {credits}
          </button>
          {subscriptionAction ? (
            <div
              className={styles.accountSubscriptionAction}
              data-sidebar-account-subscription=""
            >
              {subscriptionAction}
            </div>
          ) : null}
        </div>
      ) : (
        subscriptionAction
      )}
      <div className={styles.accountMenuHoverShell}>
        <div
          className={cn(
            styles.accountRow,
            expanded && styles.accountRowExpanded
          )}
        >
          <button
            type="button"
            className={cn(
              styles.navAvatarLink,
              expanded && styles.navAvatarLinkExpanded
            )}
            aria-label={user.name ?? accountMenuCopy.profileFallbackLabel}
            aria-expanded={isMobileViewport ? mobileOpen : undefined}
            data-sidebar-personal-center=""
            title={user.name ?? accountMenuCopy.profileFallbackLabel}
            onClick={() => {
              if (isMobileViewport) {
                setMobileOpen((current) => !current);
              }
            }}
          >
            <span className={styles.accountAvatarShell}>
              <UserAvatar
                name={user.name ?? accountMenuCopy.profileFallbackLabel}
                image={user.image}
                className={styles.navAvatar}
              />
            </span>
            {expanded ? (
              <span className={styles.accountIdentity}>
                {isPaidUser ? (
                  <PremiumGradientText className={styles.accountIdentityName}>
                    {user.name ?? accountMenuCopy.profileFallbackLabel}
                  </PremiumGradientText>
                ) : (
                  <span className={styles.accountIdentityName}>
                    {user.name ?? accountMenuCopy.profileFallbackLabel}
                  </span>
                )}
              </span>
            ) : null}
          </button>
        </div>
        <div className={styles.accountMenuPopover}>
          <CmsAccountMenuContent
            classNames={sidebarAccountMenuContentClassNames}
            onLogout={() => {
              void handleSignOut();
            }}
            onOpenSettings={openSettings}
            sections={sections}
            showUserAvatar={true}
            user={user}
            userMeta={
              <CmsWorkspaceAccountPlanCard
                accountMenuCopy={accountMenuCopy}
                credits={accountMenuCredits}
                creditsLabel={creditsLabel}
                onOpenSettings={openSettings}
                paid={isPaidUser}
                planLabel={planLabel}
                subscriptionAction={accountMenuSubscriptionAction}
              />
            }
            profileFallbackLabel={accountMenuCopy.profileFallbackLabel}
          />
        </div>
        <WorkspaceMobileDrawer
          bodyClassName={styles.mobileAccountDrawerBody}
          id="workspace-sidebar-account-drawer"
          open={isMobileViewport && mobileOpen}
          onOpenChange={(open) => setMobileOpen(open)}
          title={user.name ?? accountMenuCopy.profileFallbackLabel}
          description={user.email}
        >
          <div className={styles.mobileAccountPlanCard}>
            <CmsWorkspaceAccountPlanCard
              accountMenuCopy={accountMenuCopy}
              credits={accountMenuCredits}
              creditsLabel={creditsLabel}
              onOpenSettings={openSettings}
              paid={isPaidUser}
              planLabel={planLabel}
              subscriptionAction={accountMenuSubscriptionAction}
            />
          </div>
          <CmsAccountMenuContent
            classNames={mobileSidebarAccountMenuContentClassNames}
            onLogout={() => {
              void handleSignOut();
            }}
            onNavigate={() => setMobileOpen(false)}
            onOpenSettings={openSettings}
            sections={sections}
            showUserSummary={false}
            user={user}
            profileFallbackLabel={accountMenuCopy.profileFallbackLabel}
          />
        </WorkspaceMobileDrawer>
      </div>
    </div>
  );
}

function CmsWorkspaceSidebarAccountSkeleton({
  expanded,
  subscriptionAction,
}: {
  expanded: boolean;
  subscriptionAction?: ReactNode;
}) {
  return (
    <div
      className={cn(
        styles.accountSummary,
        expanded && styles.accountSummaryExpanded
      )}
      data-sidebar-account-skeleton={expanded ? 'expanded' : 'collapsed'}
      aria-hidden="true"
    >
      {expanded ? (
        <div className={styles.accountCommerceRow}>
          <span className={styles.accountCommerceSkeletonBalance}>
            <span
              className={cn(
                styles.accountSkeletonBlock,
                styles.accountSkeletonCreditIcon
              )}
            />
            <span
              className={cn(
                styles.accountSkeletonBlock,
                styles.accountSkeletonCreditValue
              )}
            />
          </span>
          {subscriptionAction ? (
            <div className={styles.accountSubscriptionAction}>
              {subscriptionAction}
            </div>
          ) : null}
        </div>
      ) : (
        subscriptionAction
      )}
      <div
        className={cn(
          styles.accountSkeleton,
          expanded && styles.accountSkeletonExpanded
        )}
      >
        <span
          className={cn(
            styles.accountSkeletonBlock,
            styles.accountSkeletonAvatar
          )}
        />
        {expanded ? (
          <span className={styles.accountSkeletonIdentity}>
            <span
              className={cn(
                styles.accountSkeletonBlock,
                styles.accountSkeletonName
              )}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function CmsWorkspaceSidebarAuthPanel({
  creditsLabel,
  expanded = false,
  locale,
  onNavigationAction,
  renderSubscriptionAction,
  utilityItems,
  viewerMode = 'live',
}: {
  creditsLabel: string;
  expanded?: boolean;
  locale: string;
  onNavigationAction?: (actionKey: string) => void;
  renderSubscriptionAction?: CmsWorkspaceSubscriptionActionRenderer;
  utilityItems: CmsWorkspaceSidebarUtilityItem[];
  viewerMode?: CmsShellViewerMode;
}) {
  const accountMenuCopy = useCmsAccountLocalCopy();

  return (
    <CmsShellViewerState mode={viewerMode}>
      {({
        currentUser,
        isAuthenticated,
        isPending,
        mode,
        shouldAssumeAuthenticated,
      }) => {
        if (isAuthenticated && currentUser) {
          return (
            <CmsWorkspaceSidebarAccountMenu
              accountMenuCopy={accountMenuCopy}
              accountMenuCredits={
                <CmsRemainingCreditsSlot
                  variant="account-menu"
                  label={creditsLabel}
                  previewState={
                    mode === 'authenticated'
                      ? {
                          authenticated: true,
                          availableCredits:
                            CMS_SHELL_PREVIEW_AUTHENTICATED_CREDITS,
                        }
                      : undefined
                  }
                />
              }
              credits={
                <CmsRemainingCreditsSlot
                  variant="account"
                  label={creditsLabel}
                  sidebarExpanded={expanded}
                  previewState={
                    mode === 'authenticated'
                      ? {
                          authenticated: true,
                          availableCredits:
                            CMS_SHELL_PREVIEW_AUTHENTICATED_CREDITS,
                        }
                      : undefined
                  }
                />
              }
              creditsLabel={creditsLabel}
              expanded={expanded}
              locale={locale}
              mode={mode}
              renderSubscriptionAction={renderSubscriptionAction}
              user={currentUser}
            />
          );
        }

        if (isPending || shouldAssumeAuthenticated) {
          return (
            <CmsWorkspaceSidebarAccountSkeleton
              expanded={expanded}
              subscriptionAction={renderSubscriptionAction?.()}
            />
          );
        }

        return (
          <>
            {renderSubscriptionAction?.()}
            {utilityItems.map((item) => (
              <CmsWorkspaceSidebarLoginUtilityItem
                item={item}
                key={item.id}
                onNavigationAction={onNavigationAction}
              />
            ))}
          </>
        );
      }}
    </CmsShellViewerState>
  );
}

function CmsWorkspaceSidebarLoginUtilityItem({
  item,
  onNavigationAction,
}: {
  item: CmsWorkspaceSidebarUtilityItem;
  onNavigationAction?: (actionKey: string) => void;
}) {
  if (!onNavigationAction) {
    throw new Error(
      `Workspace sidebar login action ${item.activation.actionKey} has no navigation handler.`
    );
  }

  return (
    <CmsWorkspaceSidebarNavigationLoginItem
      actionKey={item.activation.actionKey}
      label={item.label}
      onNavigationAction={onNavigationAction}
    />
  );
}

function CmsWorkspaceSidebarLoginButton({
  label,
  onLogin,
}: {
  label: string;
  onLogin: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(styles.navLink, styles.navLinkMuted, styles.loginAction)}
      data-sidebar-action="login"
      aria-label={label}
      onClick={() => {
        captureAnalyticsClientEvent('login_clicked', {
          login_surface: 'workspace_sidebar',
        });
        onLogin();
      }}
    >
      {renderAppIcon({ name: 'CircleUserRound', className: styles.navIcon })}
      <span className={styles.navText}>{label}</span>
    </button>
  );
}

function CmsWorkspaceSidebarNavigationLoginItem({
  actionKey,
  label,
  onNavigationAction,
}: {
  actionKey: string;
  label: string;
  onNavigationAction: (actionKey: string) => void;
}) {
  return (
    <CmsWorkspaceSidebarLoginButton
      label={label}
      onLogin={() => onNavigationAction(actionKey)}
    />
  );
}
